import { autoComplete } from '@/types/html';
import { ChangeEvent, ReactNode } from 'react';
import { HTMLGoogleAnalyticsData } from '@/types/globals';
import cn from 'classnames';
import { contextRowClassNames } from '../styles';
import Arrow from './Arrow';
import { ErrorTip } from '../ErrorTip';

export interface SelectProps extends HTMLGoogleAnalyticsData {
	testId?: string;
	autoComplete?: autoComplete;
	helperText?: string;
	error?: string;
	id: string;
	label: string;
	name: string;
	onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
	required?: boolean;
	disabled?: boolean;
	value?: string;
	containerProps?: React.HTMLAttributes<HTMLDivElement>;
	inputProps?: Omit<
		React.HTMLAttributes<HTMLSelectElement>,
		| 'value'
		| 'onChange'
		| 'doesntexist'
		| 'id'
		| 'name'
		| 'autoComplete'
		| 'required'
	>;
	labelProps?: Omit<React.HTMLAttributes<HTMLLabelElement>, 'htmlFor'>;
	contextRowProps?: React.HTMLAttributes<HTMLDivElement> & {
		'data-testid'?: string;
	};
	children: ReactNode;
}

const Select = (props: SelectProps) => {
	const {
		autoComplete,
		children,
		id,
		label,
		name,
		onChange,
		required,
		value,
		testId = 'Select',
		contextRowProps,
		disabled,
		containerProps,
		helperText,
		error,
		inputProps,
		labelProps,
		...gaData
	} = props;

	return (
		<div
			data-testid={testId}
			{...containerProps}
			{...gaData}
			className={cn('relative grid', containerProps?.className)}
		>
			<select
				name={name}
				id={id}
				onChange={onChange}
				value={value}
				className={cn(
					// Copied over from the styles file because the padding is different.
					`peer z-10 min-h-14 w-full appearance-none truncate rounded-xl border
					bg-transparent pb-2 pl-3 pr-8 pt-6 text-base font-normal text-slate-500
					outline-none focus:border-sky-600 focus:text-black disabled:cursor-not-allowed
					disabled:bg-slate-100 disabled:font-light disabled:text-slate-400
					[&:not(:focus):not(:checked)]:invalid:border-pink-600`,
					{ 'border-pink-600': error, 'border-slate-300': !error },
					inputProps?.className,
				)}
				autoComplete={autoComplete}
				required={required}
				aria-required={required}
				disabled={disabled}
				aria-describedby={`${id}-error-tool-tip`}
			>
				{children}
			</select>

			{/* Putting the arrow back in because it was in the wrong spot */}
			<span
				className="pointer-events-none absolute right-3 top-4.5 z-10 h-5 w-5"
				aria-hidden
			>
				{/* Hiding this element from screen readers because the user 
				doesn't need to be told there's an arrow to understand what a 
				select field is. */}
				<Arrow className="w-3" />
			</span>

			<label
				htmlFor={id}
				className={cn(
					`pointer-events-none absolute left-3 right-8 top-0 z-10 block origin-top-left
					translate-x-0 translate-y-4 scale-100 truncate text-base font-normal
					text-slate-500 transition-all duration-200 peer-disabled:text-slate-400
					peer-has-[option[value]:checked]:translate-y-2
					peer-has-[option[value]:checked]:transform
					peer-has-[option[value]:checked]:text-xs`,
					labelProps?.className,
				)}
			>
				{label}
			</label>

			<div
				{...contextRowProps}
				className={cn(contextRowClassNames, { hidden: !helperText })}
			>
				<p className={cn('text-xs font-normal text-slate-500')}>{helperText}</p>
			</div>
			{!!error && (
				<div role="alert" className="sr-only">
					One or more fields are invalid. Please review the form.
				</div>
			)}
			<ErrorTip id={`${id}-error-tool-tip`} error={error} />
		</div>
	);
};

export default Select;
